/**
 * Newsletter form — submits the customer-signup `<form>` over fetch so
 * the page doesn't reload, and renders the success / error message
 * inline. Falls back to the default form post if anything goes wrong.
 */
class NewsletterForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector("form");
    this.input = this.querySelector('input[type="email"]');
    this.button = this.querySelector('button[type="submit"]');
    this.msg = this.querySelector("[data-newsletter-msg]");
    if (!this.form) return;

    this.form.addEventListener("submit", (e) => this.#handleSubmit(e));
  }

  async #handleSubmit(e) {
    e.preventDefault();
    if (!this.form || !this.input) return;
    if (!this.input.checkValidity()) {
      this.input.reportValidity();
      return;
    }

    const originalLabel = this.button?.textContent;
    if (this.button) {
      this.button.disabled = true;
      this.button.setAttribute("aria-busy", "true");
      this.button.textContent = "…";
    }
    this.#showMsg("", null);

    try {
      const data = new FormData(this.form);
      // The 'customer' form posts to /contact (Shopify-internal endpoint
      // that handles newsletter signups too). Keep the action attribute
      // off the FormData and pull it from the form node.
      const action = this.form.getAttribute("action") || "/contact#contact_form";
      const r = await fetch(action, {
        method: "POST",
        body: data,
        headers: { Accept: "application/json,text/html" },
      });
      // Shopify sometimes returns a 302 to the same path with a flash
      // marker; both 200 and 302-followed-to-200 mean the signup was
      // accepted. Treat any 2xx response as success.
      if (!r.ok) throw new Error(`HTTP ${r.status}`);

      this.input.value = "";
      this.#showMsg("Thanks — keep an eye on your inbox.", "success");
    } catch (err) {
      console.error("Newsletter signup failed:", err);
      this.#showMsg("Something went wrong. Please try again.", "error");
    } finally {
      if (this.button) {
        this.button.disabled = false;
        this.button.removeAttribute("aria-busy");
        this.button.textContent = originalLabel ?? "Join";
      }
    }
  }

  #showMsg(text, kind) {
    if (!this.msg) return;
    // Hide any server-rendered initial message once we have a client one.
    this.querySelectorAll("[data-newsletter-msg-initial]").forEach((el) => {
      el.hidden = true;
    });
    this.msg.textContent = text;
    this.msg.hidden = !text;
    this.msg.dataset.kind = kind || "";
    this.msg.style.color = kind === "error" ? "var(--pp-peach-ink)" : "";
  }
}

customElements.define("newsletter-form", NewsletterForm);

export { NewsletterForm };
